import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  Title3,
} from "@fluentui/react-components";
import { useState } from "react";
import type { FallbackProps } from "react-error-boundary";

import { ErrorReportDialog } from "./error-report-dialog.js";

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || error.toString();
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function getErrorStack(error: unknown): string | null {
  if (error instanceof Error) {
    return error.stack || null;
  }

  return null;
}

function getReportableError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(getErrorMessage(error));
}

export const ApplicationError = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const issueMessage = getErrorMessage(error);
  const stackTrace = getErrorStack(error);
  const reportableError = getReportableError(error);
  const handleClick108 = useStableCallback(() => setIsModalOpen(true));

  const handleSuccess130 = useStableCallback(() => setSubmitted(true));

  const handleClose129 = useStableCallback(() => setIsModalOpen(false));

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gray-50 p-4">
      <Card className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto">
        <div className="p-6">
          <Title3 className="mb-3">Something went wrong</Title3>
          <p className="mb-4 text-[14px] leading-6 text-[#4b5563]">
            We encountered an unexpected problem. Please try reloading the page.
          </p>

          {/* Details accordion */}
          <div className="mb-4">
            <Accordion collapsible>
              <AccordionItem value="1">
                <AccordionHeader>Show technical details</AccordionHeader>
                <AccordionPanel>
                  <div className="max-h-[40vh] overflow-y-auto overscroll-contain rounded border border-gray-300 bg-gray-50 p-3">
                    <p className="mb-2 text-[12px] font-semibold text-gray-700">
                      Issue message:
                    </p>
                    <p className="mb-3 font-mono text-[11px] text-gray-800">
                      {issueMessage || "Unknown issue"}
                    </p>
                    {stackTrace && (
                      <>
                        <p className="mb-2 text-[12px] font-semibold text-gray-700">
                          Stack trace:
                        </p>
                        <pre className="overflow-x-auto font-mono text-[10px] break-words whitespace-pre-wrap text-gray-600">
                          {stackTrace}
                        </pre>
                      </>
                    )}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Success message */}
          {Boolean(submitted) && (
            <MessageBar className="mb-4" intent="success">
              <MessageBarBody>
                Thank you! Your issue report has been sent.
              </MessageBarBody>
            </MessageBar>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end">
            {!submitted && (
              <Button
                appearance="transparent"
                className="text-[14px] text-[#0078d4] underline hover:no-underline"
                onClick={handleClick108}
              >
                Report issue
              </Button>
            )}
            {Boolean(submitted) && <div />}
            <Button
              appearance="primary"
              onClick={resetErrorBoundary}
              type="button"
            >
              Reload
            </Button>
          </div>
        </div>
      </Card>

      {/* Error Report Dialog */}
      <ErrorReportDialog
        error={reportableError}
        isOpen={isModalOpen}
        onClose={handleClose129}
        onSuccess={handleSuccess130}
      />
    </div>
  );
};
